import axios from 'axios'

/**
 * @desc fetches products from backend
 */
export const fetchProductsFromBackend = (
  supplierId,
  status,
  page,
  size,
  query,
  brands = [],
  categories = []
) =>
  axios
    .get(`/authenticated/products/supplier/${supplierId}`, {
      params: {
        ...(status ? { status } : {}),
        ...(page !== undefined ? { page } : {}),
        ...(size !== undefined ? { size } : {}),
        ...(query ? { query } : {}),
        ...(brands.length ? { brand: brands.join(',') } : {}),
        ...(categories.length ? { category: categories.join(',') } : {})
      }
    })
    .then((res) => res.data)

export const fetchProductFilterOptions = (supplierId, status) =>
  axios
    .get(`/authenticated/products/supplier/${supplierId}/filter-options`, {
      params: { status }
    })
    .then((res) => res.data)

export const fetchProductPresenceFromBackend = (supplierId) =>
  axios
    .get(`/authenticated/products/supplier/${supplierId}/exists`)
    .then((res) => res.data)

/**
 * @desc creates a new product
 * @desc createNewProductForSupplier in BFF
 */
export const createProduct = (supplierId, payload) =>
  axios
    .post(`/authenticated/products/supplier/${supplierId}`, payload)
    .then((res) => res.data)

/**
 * @desc deletes an upcoming product
 * @desc name of API in BFF is deleteUpcomingProduct
 * @param {Integer} - supplierId
 * @param {String} - upcomingProductCode
 */
export const deleteUpcomingProduct = (supplierId, upcomingProductCode) =>
  axios
    .delete(
      `/authenticated/products/upcoming/supplier/${supplierId}/${upcomingProductCode}`
    )
    .then((res) => res.data)

/**
 * @desc permanently deletes an archived product
 * @param {Integer} - supplierId
 * @param {String} - productCode
 */
export const deleteArchivedProductPermanently = (supplierId, productCode) =>
  axios
    .delete(
      `/authenticated/products/archive/supplier/${supplierId}/${productCode}`
    )
    .then((res) => res.data)

/**
 * @desc edit product availability
 * @desc name of API in BFF is editProductAvailability
 * @param {Integer} - supplierId
 * @param {String} - productCode
 * @param {Object} - payloaf
 */
export const editProductAvailability = (supplierId, productCode, payload) =>
  axios
    .put(
      `/authenticated/products/availability/supplier/${supplierId}?productCode=${productCode}`,
      payload
    )
    .then((res) => res.data)

/**
 * @desc updates an existing product
 * @desc name of API in BFF is editProductForSupplier
 */
export const updateProduct = (supplierId, productCode, payload) =>
  axios
    .put(
      `/authenticated/products/supplier/${supplierId}?productCode=${productCode}`,
      payload
    )
    .then((res) => res.data)

/**
 * @desc updates an existing product
 * @desc name of API in BFF is editUpcomingProductForSupplier
 */
export const updateUpcomingProduct = (supplierId, productCode, payload) =>
  axios
    .put(
      `/authenticated/products/upcoming/supplier/${supplierId}?productCode=${productCode}`,
      payload
    )
    .then((res) => res.data)

/**
 * @desc uploads new product image
 */
export const uploadImage = (
  productCode,
  file,
  type = 'MAIN',
  invalidateCache = false
) => {
  const formData = new FormData()
  formData.append('file', file)

  return axios
    .post(
      `/authenticated/products/image/upload?productCode=${productCode}&type=${type}&invalidateCache=${invalidateCache}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )
    .then((res) => res.data)
}

/**
 * @desc Fetches Supplier Trading Categories
 */
export const fetchCategoriesHierarchy = () =>
  axios
    .get(`/authenticated/products/categorisation/hierarchy`)
    .then((res) => res.data)
/**
 * @desc Fetches Supplier Trading Categories
 */
export const downloadCategoriesHierarchyExcel = () =>
  axios
    .get(`/authenticated/products/categorisation/hierarchy/excel`, {
      responseType: 'blob'
    })
    .then((res) => res.data)

/**
 * @desc fetch units list
 */
export const fetchUnits = () =>
  axios
    .get('/authenticated/products/units-with-display-name')
    .then((res) => res.data)

/**
 * @desc fetch pricing regions
 */
export const fetchPricingRegions = () =>
  axios.get('/authenticated/products/pricing-regions').then((res) => res.data)

export const fetchUsTaxCodes = (search = '') =>
  axios
    .get('/authenticated/products/us-tax-codes', { params: { search } })
    .then((res) => res.data)

/**
 * @desc Fetch Dietary Infromation list
 */
export const fetchDietaryInformation = () =>
  axios
    .get('/authenticated/products/dietary-information')
    .then((res) => res.data)

/**
 * @desc Calculate pricing information for a new product (including Gross Profit %, unit prices (Tax inclusive and exclusive)
 */
export const calculateProductPrice = (payload) =>
  axios
    .post('/authenticated/pricing/calculate', payload)
    .then((res) => res.data)

/**
 * @desc getProductsBulkImportExcel (Bulk uploads)
 * @param {String} - productType
 */
export const fetchBulkImportExcel = (productType) =>
  axios
    .get(`/authenticated/products/import/excel?productType=${productType}`)
    .then((res) => res.data)

/**
 * @desc importProductsFromExcel (Bulk uploads)
 */
export const uploadBulkImportExcel = (supplierId, file) => {
  const formData = new FormData()
  formData.append('file', file)

  return axios
    .post(
      `/authenticated/products/import/excel/?supplierId=${supplierId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )
    .then((res) => res.data)
}

/**
 * @desc importPriceChangesFromExcel (Bulk price changes)
 */
export const uploadPriceChangeExcel = (file) => {
  const formData = new FormData()
  formData.append('file', file)

  return axios
    .post('/authenticated/products/price-change/import/excel', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    .then((res) => res.data)
}

/**
 * @desc publishProducts API
 * @desc Allows to set the Publish Date for Products
 * @param {string} date - date to publish products
 */
export const submitPublishDate = (supplierId, productCodes, date) =>
  axios
    .post(`/authenticated/products/publish?supplierId=${supplierId}`, {
      productCodes,
      date
    })
    .then((res) => res.data)

/**
 * @desc findProductsForSupplierAndItemCodes
 */
export const findProductsForSupplierAndItemCodes = (
  supplierId,
  itemCodes = []
) =>
  axios
    .post(`/authenticated/products/supplier/${supplierId}/find/item-codes`, {
      itemCodes
    })
    .then((res) => res.data)

export const findProductsForSupplierAndProductCodes = (
  supplierId,
  productCodes = [],
  options = {}
) =>
  axios
    .post(`/authenticated/products/supplier/${supplierId}/find/product-codes`, {
      productCodes,
      ...options
    })
    .then((res) => res.data)

/**
 * @desc archiveProducts API
 * @desc Allows to submit products for archiving
 * @param {string} date - optional, if not set applies for immediate archiving
 */
export const submitProductArchive = (supplierId, productCodes, date) =>
  axios
    .post(`/authenticated/products/archive?supplierId=${supplierId}`, {
      productCodes,
      date
    })
    .then((res) => res.data)

/**
 * @desc unarchiveProducts API
 * @desc Allows to submit products for unarchiving into draft
 * @param {string} date - optional, if not set applies for immediate unarchiving
 */
export const submitProductUnarchive = (supplierId, productCodes, date) =>
  axios
    .post(`/authenticated/products/unarchive?supplierId=${supplierId}`, {
      productCodes,
      date
    })
    .then((res) => res.data)

export const uploadDraftImages = (files, onUploadProgress) => {
  const formData = new FormData()
  files.forEach((file) => formData.append('files', file))
  return axios
    .post('/authenticated/products/image/upload/draft', formData, {
      onUploadProgress
    })
    .then((res) => res.data)
}
